import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { trackEvent } from "@/utils/analytics";
import { Toaster } from "sonner";
import { StoreProvider, useStore } from "@/context/StoreContext";
import { AppDialogProvider } from "@/components/AppDialog";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import ScrollToTop from "@/components/ScrollToTop";
import BragOnWin from "@/components/BragOnWin";
import PlaytimeTracker from "@/components/PlaytimeTracker";
import Welcome from "@/pages/Welcome";
import Home from "@/pages/Home";
import Category from "@/pages/Category";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Wishlist from "@/pages/Wishlist";
import Orders from "@/pages/Orders";
import Games from "@/pages/Games";
import GamePlay from "@/pages/GamePlay";
import Stickers from "@/pages/Stickers";
import Profile from "@/pages/Profile";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminAds from "@/pages/AdminAds";
import AuthCallback from "@/pages/AuthCallback";
import Leaderboard from "@/pages/Leaderboard";
import { Privacy, Terms, Dmca, LegalNotices } from "@/pages/Legal";
import Footer from "@/components/Footer";

const RequireName = ({ children }) => {
  const { name } = useStore();
  if (!name) return <Navigate to="/welcome" replace />;
  return children;
};

// Returning visitors (with a saved name) skip the welcome screen and land straight on the shop.
const WelcomeGate = () => {
  const { name } = useStore();
  if (name) return <Navigate to="/shop" replace />;
  return <Welcome />;
};

// Root: returning users → shop; new users → welcome.
const RootRoute = () => {
  const { name } = useStore();
  return name ? <Navigate to="/shop" replace /> : <Navigate to="/welcome" replace />;
};

const ShellRoute = ({ children }) => (
  <RequireName>
    <div className="min-h-screen flex flex-col pb-28 lg:pb-8">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <BottomNav />
      <BragOnWin />
      <PlaytimeTracker />
    </div>
  </RequireName>
);

// Legal pages are accessible without logging in (kids-and-parents friendly),
// but still show the header/footer for consistency when the visitor IS in.
const LegalShell = ({ children }) => {
  const { name } = useStore();
  return (
    <div className="min-h-screen flex flex-col pb-8">
      {name && <Header />}
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// Fire a `page_view` analytics event on every route change (skip admin routes).
const RouteTracker = () => {
  const loc = useLocation();
  const lastRef = useRef("");
  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) return;
    // Guard against React 18 StrictMode's double effect invocation which
    // would otherwise inflate counters. Idempotent per unique pathname.
    if (lastRef.current === loc.pathname) return;
    lastRef.current = loc.pathname;
    trackEvent("page_view", { meta: { path: loc.pathname } });
  }, [loc.pathname]);
  return null;
};

function App() {
  return (
    <StoreProvider>
      <AppDialogProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RouteTracker />
        <Toaster
          position="top-center"
          offset={80}
          visibleToasts={2}
          duration={1800}
          toastOptions={{
            style: {
              background: "#FFF9E6",
              color: "#484848",
              border: "3px solid #2b2b2b",
              borderRadius: 20,
              fontFamily: "Fredoka, sans-serif",
              boxShadow: "0 5px 0 #2b2b2b",
            },
          }}
        />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/welcome" element={<WelcomeGate />} />
          <Route
            path="/shop"
            element={
              <ShellRoute>
                <Home />
              </ShellRoute>
            }
          />
          <Route
            path="/category/:id"
            element={
              <ShellRoute>
                <Category />
              </ShellRoute>
            }
          />
          <Route
            path="/product/:id"
            element={
              <ShellRoute>
                <Product />
              </ShellRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <ShellRoute>
                <Cart />
              </ShellRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ShellRoute>
                <Checkout />
              </ShellRoute>
            }
          />
          <Route
            path="/wishlist"
            element={
              <ShellRoute>
                <Wishlist />
              </ShellRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ShellRoute>
                <Orders />
              </ShellRoute>
            }
          />
          <Route
            path="/games"
            element={
              <ShellRoute>
                <Games />
              </ShellRoute>
            }
          />
          <Route
            path="/games/:id"
            element={
              <ShellRoute>
                <GamePlay />
              </ShellRoute>
            }
          />
          <Route
            path="/stickers"
            element={
              <ShellRoute>
                <Stickers />
              </ShellRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ShellRoute>
                <Profile />
              </ShellRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ShellRoute>
                <Leaderboard />
              </ShellRoute>
            }
          />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/privacy" element={<LegalShell><Privacy /></LegalShell>} />
          <Route path="/terms" element={<LegalShell><Terms /></LegalShell>} />
          <Route path="/dmca" element={<LegalShell><Dmca /></LegalShell>} />
          <Route path="/legal-notices" element={<LegalShell><LegalNotices /></LegalShell>} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/users/:id" element={<AdminUserDetail />} />
          <Route path="/admin/ads" element={<AdminAds />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AppDialogProvider>
    </StoreProvider>
  );
}

export default App;
